import { appConfig } from './config';

type RumEnvironment = 'prod' | 'gray' | 'pre' | 'daily' | 'local';

type PublicMonitoringConfig = {
  enabled: boolean;
  endpoint?: string;
  environment: RumEnvironment;
  sessionSampleRate: number;
  traceSampleRate: number;
};

let initialization: Promise<void> | undefined;

const clampRate = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(1, parsed)) : fallback;
};

const normalizeEnvironment = (value?: string): RumEnvironment => {
  const normalized = value?.trim().toLowerCase();
  return normalized === 'prod' || normalized === 'gray' || normalized === 'pre'
    || normalized === 'daily' || normalized === 'local'
    ? normalized
    : 'prod';
};

const fallbackConfig = (): PublicMonitoringConfig => ({
  enabled: process.env.REACT_APP_ARMS_RUM_ENABLED === 'true',
  endpoint: process.env.REACT_APP_ARMS_RUM_ENDPOINT?.trim() || undefined,
  environment: normalizeEnvironment(process.env.REACT_APP_ARMS_RUM_ENVIRONMENT),
  sessionSampleRate: clampRate(process.env.REACT_APP_ARMS_RUM_SESSION_SAMPLE_RATE, 0.1),
  traceSampleRate: clampRate(process.env.REACT_APP_ARMS_RUM_TRACE_SAMPLE_RATE, 0.05),
});

const loadConfig = async (): Promise<PublicMonitoringConfig> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 2500);
  try {
    const response = await fetch(`${appConfig.apiBaseUrl}/api/system/monitoring-config?client=web`, {
      signal: controller.signal,
      credentials: 'omit',
    });
    if (!response.ok) throw new Error(`Monitoring config returned ${response.status}`);
    const payload = await response.json() as { data?: Partial<PublicMonitoringConfig> };
    const data = payload.data;
    if (!data) throw new Error('Monitoring config is missing');
    return {
      enabled: data.enabled === true,
      endpoint: data.endpoint?.trim() || undefined,
      environment: normalizeEnvironment(data.environment),
      sessionSampleRate: clampRate(data.sessionSampleRate, 0.1),
      traceSampleRate: clampRate(data.traceSampleRate, 0.05),
    };
  } catch {
    return fallbackConfig();
  } finally {
    window.clearTimeout(timeout);
  }
};

const sensitiveKey = /authorization|cookie|password|passwd|token|secret|api[-_]?key|private[-_]?key/i;
const urlKey = /^(url|referrer|file)$/i;

const sanitizeValue = (value: unknown, key = '', seen = new WeakSet<object>()): unknown => {
  if (sensitiveKey.test(key)) return '[REDACTED]';
  if (typeof value === 'string') {
    if (urlKey.test(key)) {
      try {
        const url = new URL(value, window.location.origin);
        url.search = '';
        url.hash = '';
        return url.toString();
      } catch {
        return value.split(/[?#]/, 1)[0];
      }
    }
    return value.replace(/(authorization|password|token|secret|api[-_]?key)\s*[:=]\s*[^\s,;]+/gi, '$1=[REDACTED]');
  }
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return '[CIRCULAR]';
  seen.add(value);
  if (Array.isArray(value)) return value.map(item => sanitizeValue(item, key, seen));
  return Object.fromEntries(Object.entries(value).map(([childKey, childValue]) => [
    childKey,
    childKey === 'snapshots' || childKey === 'records_data'
      ? undefined
      : sanitizeValue(childValue, childKey, seen),
  ]));
};

export const initializeWebMonitoring = () => {
  initialization ??= (async () => {
    const config = await loadConfig();
    if (!config.enabled || !config.endpoint) return;
    const { default: armsRum } = await import('@arms/rum-browser');
    const tracedApiOrigin = appConfig.apiBaseUrl || window.location.origin;
    await armsRum.init({
      endpoint: config.endpoint,
      env: config.environment,
      version: process.env.REACT_APP_RELEASE ?? '1.0.0',
      app: { name: 'guanzhai-web', framework: 'react', type: 'web' },
      spaMode: 'history',
      sessionConfig: { sampling: config.sessionSampleRate * 100 },
      collectors: { click: false },
      replay: false,
      tracing: {
        enable: true,
        sample: config.traceSampleRate * 100,
        propagatorTypes: ['tracecontext'],
        allowedUrls: [{ match: tracedApiOrigin, propagatorTypes: ['tracecontext'] }],
      },
      beforeReport: bundle => sanitizeValue(bundle),
    });
  })().catch(error => {
    if (process.env.NODE_ENV !== 'production') console.warn('ARMS Web monitoring initialization failed', error);
  });
  return initialization;
};
